import { useState } from 'react'
import { Autocomplete, Box, Button, Stack, TextField } from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import type { OrganizationListItemResponse } from '@portal/pkg-api/gen/admin-console'
import { OrganizationQueries } from '../../organization'
import { OrgAdminForm } from './OrgAdminForm'

interface OrgBarProps {
  selectedOrgId: number | null
  onOrgChange: (orgId: number | null) => void
}

export function OrgBar({ selectedOrgId, onOrgChange }: OrgBarProps) {
  const { t } = useTranslation('admin-console')
  const [isCreateOrgOpen, setIsCreateOrgOpen] = useState(false)
  const [isEditOrgOpen, setIsEditOrgOpen] = useState(false)
  const { data: orgs = [], isLoading } = useQuery(OrganizationQueries.list())
  const selectedOrg = orgs.find((o) => o.id === selectedOrgId) ?? null

  return (
    <Box
      sx={{
        display: 'flex',
        width: '100%',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 2,
        px: 2,
        py: 1.5,
        backgroundColor: 'grey.50',
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
      }}
    >
      <Autocomplete<OrganizationListItemResponse, false, boolean>
        size="small"
        options={orgs}
        value={selectedOrg}
        disableClearable
        loading={isLoading}
        onChange={(_, org) => {
          if (org) {
            onOrgChange(org.id)
          }
        }}
        getOptionLabel={(org) => t('users.orgBar.orgLabel', { name: org.name, id: org.id })}
        isOptionEqualToValue={(opt, val) => opt.id === val.id}
        renderInput={(params) => <TextField {...params} label={t('users.orgBar.selectOrg')} />}
        sx={{ minWidth: 280 }}
        noOptionsText={t('users.orgBar.noOrgs')}
        loadingText={t('users.orgBar.loadingOrgs')}
      />
      <Box sx={{ flexGrow: 1 }} />
      <Stack direction="row" spacing={1}>
        <Button
          variant="outlined"
          size="small"
          disabled={!selectedOrgId}
          onClick={() => setIsEditOrgOpen(true)}
        >
          {t('users.orgBar.editOrg')}
        </Button>
        <Button variant="contained" size="small" onClick={() => setIsCreateOrgOpen(true)}>
          {t('users.orgBar.newOrg')}
        </Button>
      </Stack>
      {isCreateOrgOpen && <OrgAdminForm onClose={() => setIsCreateOrgOpen(false)} />}
      {isEditOrgOpen && selectedOrgId && (
        <OrgAdminForm orgId={selectedOrgId} onClose={() => setIsEditOrgOpen(false)} />
      )}
    </Box>
  )
}
